import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Loader2, AlertCircle } from 'lucide-react';
import { importSourcesApi } from '../../../api/importSourcesApi';
import { formatModeratorApiKeyError } from '../../../utils/apiClient';
import { useUiLocale } from '../../../contexts/UiLocaleContext';

type DocMeta = {
  id: number;
  file_name: string;
  file_type: string;
  file_size?: number;
  status: string;
  character_count?: number;
  extracted_text?: string;
  preview?: string;
  raw_data_id?: number;
  error_message?: string;
};

/**
 * Unified in-app document viewer (Drive/Notion style):
 * - PDF → embedded browser viewer
 * - DOCX / other office → extracted text body + download original
 * Excel URL-list imports are not shown here: each crawled URL is a normal article link.
 */
export function DocumentPreviewPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const { t } = useUiLocale();
  const id = Number(documentId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [doc, setDoc] = useState<DocMeta | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isFinite(id) || id <= 0) {
      setError('Invalid document id');
      setLoading(false);
      return;
    }

    let objectUrl: string | null = null;
    const ac = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await importSourcesApi.getDocument(id, { signal: ac.signal });
        if (ac.signal.aborted) return;
        setDoc(data);

        const type = (data.file_type || '').toLowerCase();
        if (type === 'pdf') {
          const blobRes = await importSourcesApi.downloadDocument(id, { signal: ac.signal });
          if (ac.signal.aborted) return;
          const blob =
            blobRes.data instanceof Blob
              ? new Blob([blobRes.data], { type: 'application/pdf' })
              : new Blob([blobRes.data], { type: 'application/pdf' });
          objectUrl = URL.createObjectURL(blob);
          setPdfUrl(objectUrl);
        }
      } catch (err: unknown) {
        if (ac.signal.aborted) return;
        setError(formatModeratorApiKeyError(err));
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();

    return () => {
      ac.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  const handleDownloadOriginal = async () => {
    if (!doc) return;
    try {
      const res = await importSourcesApi.downloadDocument(doc.id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `document-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(formatModeratorApiKeyError(err));
    }
  };

  const fileType = (doc?.file_type || '').toLowerCase();
  const textBody = (doc?.extracted_text || doc?.preview || '').trim();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to="/collected-raw"
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('dataPool.title') || 'Data Pool'}
            </Link>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {doc?.file_name || `Document #${documentId}`}
              </p>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">
                {(fileType || 'file').toUpperCase()}
                {doc?.character_count != null ? ` · ${doc.character_count.toLocaleString()} chars` : ''}
              </p>
            </div>
          </div>
          {doc && (
            <button
              type="button"
              onClick={handleDownloadOriginal}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800"
            >
              <Download className="h-3.5 w-3.5" />
              Download original
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-4">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-24 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading preview…
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {!loading && !error && doc && fileType === 'pdf' && pdfUrl && (
          <iframe
            title={doc.file_name}
            src={pdfUrl}
            className="h-[calc(100vh-7rem)] w-full rounded-xl border border-slate-200 bg-white shadow-sm"
          />
        )}

        {!loading && !error && doc && fileType !== 'pdf' && (
          <article className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-slate-700">
              <FileText className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium">
                Extracted text preview
                {fileType === 'docx' ? ' (Word)' : fileType === 'xlsx' || fileType === 'xls' ? ' (Excel)' : ''}
              </span>
            </div>
            {textBody ? (
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                {textBody}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                No extractable text for this file. Use “Download original” to open it in Word/Excel.
              </p>
            )}
          </article>
        )}
      </main>
    </div>
  );
}
